"use client";
import React, { useEffect, useState } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import styles from "./SingupModule.module.css";
import * as Yup from "yup";
import Button from "../../../components/atoms/Button/Button";
import dayjs from "../../../utils/dayjs";
import InputField from "../../../components/atoms/InputField/InputField";
import ValidationErrors, {
  Validation,
} from "@/components/atoms/ValidationErrors/ValidationErrors";

interface Values {
  full_name: string;
  email: string;
  password: string;
}

const errorOrUnedfined = (
  touched?: boolean,
  error?: string
): string | undefined => {
  return !!touched ? error : undefined;
};

const default_validtions: Record<string, Validation> = {
  length: {
    id: "length",
    title: "At least 8 characters",
    pass: false,
  },
  lowercase: {
    id: "lowercase",
    title: "Lower case letters (a-z)",
    pass: false,
  },
  uppercase: {
    id: "uppercase",
    title: "Upper case letters (A-Z)",
    pass: false,
  },
  numbers: {
    id: "numbers",
    title: "Numbers (0-9)",
    pass: false,
  },
};

const SignupSchema = Yup.object().shape({
  full_name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "NAME MUST BE LESS THAN 50 CHARACTERS LONG"),
  email: Yup.string().email("Invalid email").required(),
  password: Yup.string()
    .min(8, "PASSWORD MUST BE AT LEAST 8 CHARACTERS LONG")
    .required(),
});

export type SessionInfo = {
  duration: number;
  start_time: string;
  full_name: string;
};
export interface SignupModuleProps {
  session_info: SessionInfo;
  changeDuration: () => void;
  handleSignup: (
    values: Values,
    setSubmitting: FormikHelpers<Values>["setSubmitting"]
  ) => void;
  goToLogin: () => void;
}

const SignupModule = ({
  session_info,
  changeDuration,
  handleSignup,
  goToLogin,
}: SignupModuleProps) => {
  const { duration, full_name, start_time } = session_info;

  const formatStartDate = (datetime: string) => {
    const day = dayjs(datetime);
    return day.format("ddd MMM D, h:mma z");
  };

  const [password_validations, setValidations] =
    useState<Record<string, Validation>>(default_validtions);

  const cusomtValidator = (
    values: Values
  ): Record<string, string | undefined> => {
    // console.log("validation", result);
    password_validations["length"].pass = values.password.length >= 8;
    password_validations["lowercase"].pass = !!values.password.match(/[a-z]/);
    password_validations["uppercase"].pass = !!values.password.match(/[A-Z]/);
    password_validations["numbers"].pass = !!values.password.match(/[0-9]/);
    setValidations(password_validations);

    if(full_name.length < 2) {
      return { full_name: "NAME MUST BE AT LEAST 2 CHARACTERS LONG" };
    }

    if(full_name.length > 50) {
      return { full_name: "NAME MUST BE LESS THAN 50 CHARACTERS LONG" };
    }

    if (!values.email) {
      return { email: "REQUIRED" };
    }

    return {};
  };

  return (
    <div className={styles.signup_module}>
      <div className={styles.info_container}>
        <h3>Sign up to continue booking</h3>
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M8.00016 14.6667C4.31826 14.6667 1.3335 11.6819 1.3335 8.00004C1.3335 4.31814 4.31826 1.33337 8.00016 1.33337C11.682 1.33337 14.6668 4.31814 14.6668 8.00004C14.6668 11.6819 11.682 14.6667 8.00016 14.6667ZM8.00016 13.3334C10.9457 13.3334 13.3335 10.9456 13.3335 8.00004C13.3335 5.05452 10.9457 2.66671 8.00016 2.66671C5.05464 2.66671 2.66683 5.05452 2.66683 8.00004C2.66683 10.9456 5.05464 13.3334 8.00016 13.3334ZM8.66683 8.00004H11.3335V9.33337H7.3335V4.66671H8.66683V8.00004Z"
              fill="#BFBFBF"
            />
          </svg>
          <p>
            {duration} mins with {full_name}
          </p>
        </div>
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M6.00016 0.666626V1.99996H10.0002V0.666626H11.3335V1.99996H14.0002C14.3684 1.99996 14.6668 2.29844 14.6668 2.66663V13.3333C14.6668 13.7015 14.3684 14 14.0002 14H2.00016C1.63198 14 1.3335 13.7015 1.3335 13.3333V2.66663C1.3335 2.29844 1.63198 1.99996 2.00016 1.99996H4.66683V0.666626H6.00016ZM13.3335 7.33329H2.66683V12.6666H13.3335V7.33329ZM4.66683 3.33329H2.66683V5.99996H13.3335V3.33329H11.3335V4.66663H10.0002V3.33329H6.00016V4.66663H4.66683V3.33329Z"
              fill="#BFBFBF"
            />
          </svg>
          <p>{formatStartDate(start_time)}</p>{" "}
          <span onClick={changeDuration}>Change time?</span>
        </div>
      </div>
      <div className={styles.divider} />
      <Formik
        initialValues={{
          full_name: "",
          email: "",
          password: "",
        }}
        // validationSchema={SignupSchema}
        validate={cusomtValidator}
        onSubmit={(values, { setSubmitting }) => {
          handleSignup(values, setSubmitting);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <Form onSubmit={handleSubmit} className={styles.form}>
            <InputField
              label="Full Name"
              type="text"
              name="full_name"
              value={values.full_name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errorOrUnedfined(touched.full_name, errors.full_name)}
            />
            <InputField
              label="Email"
              type="email"
              name="email"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errorOrUnedfined(touched.email, errors.email)}
            />
            {/* <InputField
              label="Password"
              type="password"
              name="password"
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errorOrUnedfined(touched.password, errors.password)}
            />
            <ValidationErrors
              id="signup_errors"
              description="A password must have:"
              validations={Object.values(password_validations)}
            /> */}
            <Button
              className={styles.signup_btn}
              data-testid="signup-btn"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              Sign up
            </Button>
            <p className={styles.go_to_login} onClick={goToLogin}>
              Already have an account?
            </p>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SignupModule;
